import type { AnswerGroup, AnsweredQuestion, Citation } from "@/api/q-and-a";
import { isGraphSourcedAnswer } from "./graphCitations";

export interface AnswerGroupStats {
  inProgress: number;
  completed: number;
  unavailable: number;
  total: number;
  answerCount: Record<string, number>;
}

export interface FilteredAnswerGroups {
  groups: AnswerGroup[];
  stats: AnswerGroupStats;
}

/**
 * Filters answer groups to only include those with answers from a specific document
 * and calculates statistics about answer status
 */
export function filterAnswerGroupsForDocument(
  answerGroups: AnswerGroup[],
  documentId?: string
): FilteredAnswerGroups {
  // Filter groups to only include those with citations from the specified document
  const filteredGroups = documentId
    ? answerGroups.filter((group) =>
        group.answers.some((answer) =>
          answer.citations.some((citation) => citation.document_id === documentId)
        )
      )
    : answerGroups;

  // Calculate statistics for the filtered groups
  let inProgress = 0;
  let completed = 0;
  let unavailable = 0;
  const answerCount: Record<string, number> = {};

  filteredGroups.forEach((group) => {
    const answers = group.answers;
    
    // Check if any answer in the group is in progress
    const groupInProgress = answers.some((ans) => ans.in_progress);
    if (groupInProgress) {
      inProgress += 1;
      return;
    }
    
    // Find the first answer with text (completed answer)
    const answerWithText = answers.find((ans) => ans.text && ans.text.trim() !== "");
    if (answerWithText) {
      completed += 1;
      if (answerWithText.text) {
        if (answerCount[answerWithText.text] !== undefined) {
          answerCount[answerWithText.text] += 1;
        } else {
          answerCount[answerWithText.text] = 1;
        }
      }
      return;
    }
    
    // No answer with text found - mark as unavailable
    unavailable += 1;
  });

  return {
    groups: filteredGroups,
    stats: {
      inProgress,
      completed,
      unavailable,
      total: filteredGroups.length,
      answerCount,
    },
  };
}

// Minimum length before we'll collapse two graph answers by containment — keeps
// trivially-short answers from swallowing each other; real graph_full answers
// are full sentences well above this.
const GRAPH_CONTAINMENT_MIN_LEN = 20;

const displayAnswerOf = (group: AnswerGroup) =>
  group.answers.find((a) => a.is_composite) ?? group.answers[0];

const displayTextOf = (group: AnswerGroup): string =>
  (displayAnswerOf(group)?.text ?? "").trim().toLowerCase();

export interface DedupedAnswerGroups {
  displayGroups: AnswerGroup[];
  extraCitationsMap: Map<string, Citation[]>;
}

/**
 * Collapse duplicate answer groups for display:
 *  - exact-text duplicates (any answer type), and
 *  - near-identical GRAPH answers where one's text fully contains the other's.
 *    graph_full can emit a short answer AND a more detailed one for the same
 *    question in a single run (two answer groups); we keep the richer and don't
 *    render it twice. Non-graph answers only collapse on exact text — a shorter
 *    extraction can be a legitimately distinct value.
 *
 * Returns the surviving groups plus a map of winner-group-id -> citations
 * carried over from the collapsed groups, so the winner still shows every
 * source. (The winner's own citations are NOT added to this map — callers
 * render them directly — which avoids double-counting.)
 */
export function dedupeAnswerGroupsForDisplay(
  answerGroups: AnswerGroup[],
): DedupedAnswerGroups {
  const displayGroups: AnswerGroup[] = [];
  const extraCitationsMap = new Map<string, Citation[]>();

  const carry = (winner: AnswerGroup, cits: Citation[]) => {
    // A DocumentAnswer can belong to multiple AnswerGroups (M2M), so a collapsed
    // duplicate can share answers — and therefore citation rows with the same
    // citation.id — with the winner. Skip ids the winner already renders (own
    // citations or previously carried extras) so one row never shows a citation
    // twice; callers concatenate own + extras without re-checking.
    const seen = new Set([
      ...winner.answers.flatMap((a) => a.citations.map((c) => c.id)),
      ...(extraCitationsMap.get(winner.id) ?? []).map((c) => c.id),
    ]);
    const fresh: Citation[] = [];
    for (const c of cits) {
      if (!seen.has(c.id)) {
        seen.add(c.id);
        fresh.push(c);
      }
    }
    if (fresh.length === 0) return;
    extraCitationsMap.set(winner.id, [
      ...(extraCitationsMap.get(winner.id) ?? []),
      ...fresh,
    ]);
  };

  for (const group of answerGroups) {
    const text = displayTextOf(group);
    if (!text) {
      displayGroups.push(group); // "not found" / empty — never collapsed
      continue;
    }
    const isGraph = isGraphSourcedAnswer(displayAnswerOf(group));

    const matchIdx = displayGroups.findIndex((kept) => {
      const keptText = displayTextOf(kept);
      if (!keptText) return false;
      if (keptText === text) return true;
      return (
        isGraph &&
        isGraphSourcedAnswer(displayAnswerOf(kept)) &&
        Math.min(text.length, keptText.length) >= GRAPH_CONTAINMENT_MIN_LEN &&
        (text.includes(keptText) || keptText.includes(text))
      );
    });

    if (matchIdx === -1) {
      displayGroups.push(group);
      continue;
    }

    const kept = displayGroups[matchIdx];
    // Winner = the current value if either is; otherwise the richer (longer).
    const incomingWins =
      (group.is_current_value && !kept.is_current_value) ||
      (!group.is_current_value &&
        !kept.is_current_value &&
        text.length > displayTextOf(kept).length);

    if (incomingWins) {
      const keptExtras = extraCitationsMap.get(kept.id) ?? [];
      extraCitationsMap.delete(kept.id);
      displayGroups[matchIdx] = group;
      carry(group, [...kept.answers.flatMap((a) => a.citations), ...keptExtras]);
    } else {
      carry(kept, group.answers.flatMap((a) => a.citations));
    }
  }

  return { displayGroups, extraCitationsMap };
}

/**
 * Find a citation by id within a document across all questions — used for the
 * "page-first" deep-link landing (scroll to the citation's page immediately,
 * before its highlight has finished processing).
 */
export function findCitationByIdInDocument(
  questions: AnsweredQuestion[],
  citationId: string,
  documentId?: string,
): Citation | undefined {
  for (const q of questions) {
    for (const g of q.answer_groups) {
      for (const a of g.answers) {
        const c = a.citations.find(
          (cit) =>
            cit.id === citationId &&
            (!documentId || cit.document_id === documentId),
        );
        if (c) return c;
      }
    }
  }
  return undefined;
}

/**
 * Filters answer groups to only include those with found answers (not "not found")
 */
export function filterFoundAnswerGroups(answerGroups: AnswerGroup[]): AnswerGroup[] {
  return answerGroups.filter((group) =>
    group.answers.some((answer) => answer.text && answer.text.trim() !== "")
  );
}

/**
 * Checks if an answer group has any answers with text (i.e., found answers)
 */
export function hasFoundAnswers(answerGroup: AnswerGroup): boolean {
  return answerGroup.answers.some((answer) => answer.text && answer.text.trim() !== "");
}

/**
 * Gets the first found answer from an answer group
 */
export function getFirstFoundAnswer(answerGroup: AnswerGroup) {
  return answerGroup.answers.find((answer) => answer.text && answer.text.trim() !== "");
}

/**
 * Filters answers within a group to only include those with citations from a specific document
 */
export function filterAnswersForDocument(answerGroup: AnswerGroup, documentId: string): AnswerGroup {
  return {
    ...answerGroup,
    answers: answerGroup.answers.filter((answer) =>
      answer.citations.some((citation) => citation.document_id === documentId)
    ),
  };
}

/**
 * Pre-filters questions to only include answer groups and answers that have citations from a specific document
 * This is more comprehensive than component-level filtering
 */
export function filterQuestionsForDocument(
  questions: AnsweredQuestion[],
  documentId?: string
): AnsweredQuestion[] {
  // If no documentId provided or it's empty, return all questions
  if (!documentId || documentId.trim() === "") {
    return questions;
  }

  const filtered = questions
    .map((question) => {
      // Filter answer groups to only include those with at least one citation from this document
      const filteredGroups = question.answer_groups
        .filter((group) =>
          // Keep the group if ANY answer has ANY citation from this document
          group.answers.some((answer) =>
            answer.citations.some((citation) => citation.document_id === documentId)
          )
        );

      return {
        ...question,
        answer_groups: filteredGroups,
      };
    });

  return filtered;
}

/**
 * Find the first citation in a document for a specific answer group
 * @param questions - Array of answered questions to search through
 * @param groupId - The answer group ID to find citations for
 * @param documentId - The document ID to filter citations by
 * @returns The first citation found, or undefined if none found
 */
export function findFirstCitationForGroupInDocument(
  questions: AnsweredQuestion[],
  groupId: string,
  documentId?: string
): { citation: string; page: number | null; id: string } | undefined {
  if (!documentId) return undefined;
  
  // Find the question that contains the target group
  const targetQuestion = questions.find((question) =>
    question.answer_groups.some((group) => group.id === groupId)
  );

  if (!targetQuestion) return undefined;

  // Find the specific answer group
  const targetGroup = targetQuestion.answer_groups.find(
    (group) => group.id === groupId
  );

  if (!targetGroup) return undefined;

  // Find the first answer with citations in this document
  const firstAnswerWithCitation = targetGroup.answers.find((answer) =>
    answer.citations.some((citation) => citation.document_id === documentId)
  );

  if (!firstAnswerWithCitation) return undefined;

  // Find the first citation in this document
  const firstCitationInDoc = firstAnswerWithCitation.citations.find(
    (citation) => citation.document_id === documentId
  );

  if (!firstCitationInDoc) return undefined;

  return {
    citation: firstCitationInDoc.citation ?? "",
    page: firstCitationInDoc.page,
    id: firstCitationInDoc.id,
  };
}
